import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckInPhoto } from '../../types';
import { photoService } from '../../services/endpoints/photoService';
import PhotoLightbox from './PhotoLightbox';

const MAX_PHOTOS = 10;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];

interface Props {
  metricId: string;
  /** Passed by coach views — enables read-only mode and proxies requests. */
  viewAs?: string;
  /** When true, upload / delete controls are hidden. Set for coach views. */
  readOnly?: boolean;
}

const CheckInPhotoSection: React.FC<Props> = ({ metricId, viewAs, readOnly = false }) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Photo metadata from the API
  const { data: photos = [], isLoading } = useQuery<CheckInPhoto[]>({
    queryKey: ['checkin-photos', metricId, viewAs],
    queryFn:  () => photoService.getPhotos(metricId, viewAs),
    enabled:  !!metricId,
  });

  // Object URLs keyed by photoId — loaded lazily and cleaned up on unmount
  const [objectUrls, setObjectUrls] = useState<Record<string, string>>({});
  const [urlsLoading, setUrlsLoading] = useState(false);

  useEffect(() => {
    if (photos.length === 0) return;

    let cancelled = false;
    setUrlsLoading(true);

    const load = async () => {
      const newUrls: Record<string, string> = {};
      for (const p of photos) {
        if (!objectUrls[p.photoId]) {
          try {
            newUrls[p.photoId] = await photoService.loadPhotoAsObjectUrl(p.photoId, viewAs);
          } catch {
            // continue loading remaining photos if one fails
          }
        }
      }
      if (!cancelled) {
        setObjectUrls(prev => ({ ...prev, ...newUrls }));
        setUrlsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos, viewAs]);

  // Revoke all object URLs on unmount to free memory
  useEffect(() => {
    return () => {
      Object.values(objectUrls).forEach(url => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lightbox state
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Inline delete confirmation for thumbnails
  const [confirmDeletePhoto, setConfirmDeletePhoto] = useState<CheckInPhoto | null>(null);

  // Batch upload state
  const [batchUpload, setBatchUpload] = useState<{ current: number; total: number } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadWarn, setUploadWarn]   = useState<string | null>(null);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (photo: CheckInPhoto) => photoService.deletePhoto(photo.photoId),
    onSuccess: (_data, photo) => {
      if (objectUrls[photo.photoId]) {
        URL.revokeObjectURL(objectUrls[photo.photoId]);
        setObjectUrls(prev => {
          const next = { ...prev };
          delete next[photo.photoId];
          return next;
        });
      }
      setLightboxIndex(null);
      queryClient.invalidateQueries({ queryKey: ['checkin-photos', metricId] });
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
    },
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Convert to a plain array BEFORE resetting the input — setting value='' clears
    // the browser's FileList reference, so we must snapshot it first.
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // reset so the same selection can be triggered again

    if (files.length === 0) return;

    setUploadError(null);
    setUploadWarn(null);

    // Validate each file — collect valid ones and error messages
    const valid: File[] = [];
    const validationErrors: string[] = [];

    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type.toLowerCase())) {
        validationErrors.push(`"${file.name}" is not a supported image type`);
      } else if (file.size > MAX_FILE_BYTES) {
        validationErrors.push(`"${file.name}" exceeds the 10 MB size limit`);
      } else {
        valid.push(file);
      }
    }

    if (validationErrors.length > 0) {
      setUploadError(validationErrors.join('. '));
      if (valid.length === 0) return;
    }

    // Enforce 10-photo cap — trim excess files and warn
    const available = MAX_PHOTOS - photos.length;
    const toUpload  = valid.slice(0, available);
    const skipped   = valid.length - toUpload.length;

    if (toUpload.length === 0) {
      setUploadError(`Maximum of ${MAX_PHOTOS} photos reached — no photos were added.`);
      return;
    }

    // Upload sequentially, invalidating the cache after each success
    setBatchUpload({ current: 0, total: toUpload.length });

    for (let i = 0; i < toUpload.length; i++) {
      setBatchUpload({ current: i + 1, total: toUpload.length });
      try {
        await photoService.uploadPhoto(metricId, toUpload[i]);
        queryClient.invalidateQueries({ queryKey: ['checkin-photos', metricId] });
        queryClient.invalidateQueries({ queryKey: ['metrics'] });
      } catch (err) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        setUploadError(msg ?? `Failed to upload "${toUpload[i].name}". Please try again.`);
        setBatchUpload(null);
        return; // stop on first error
      }
    }

    setBatchUpload(null);

    if (skipped > 0) {
      setUploadWarn(
        `${skipped} photo${skipped > 1 ? 's were' : ' was'} not added — the 10-photo limit was reached.`
      );
    }
  };

  // Lightbox delete: confirmation is handled inside PhotoLightbox
  const handleDeleteFromLightbox = (photo: CheckInPhoto) => {
    deleteMutation.mutate(photo);
  };

  // Thumbnail × click: show inline confirm panel
  const handleDeleteFromThumbnail = (e: React.MouseEvent, photo: CheckInPhoto) => {
    e.stopPropagation();
    setConfirmDeletePhoto(photo);
  };

  const handleConfirmThumbnailDelete = () => {
    if (confirmDeletePhoto) {
      deleteMutation.mutate(confirmDeletePhoto);
      setConfirmDeletePhoto(null);
    }
  };

  const isUploading = batchUpload !== null;
  const canAddMore  = !readOnly && photos.length < MAX_PHOTOS;

  return (
    <div className="mt-4">
      {/* Section header */}
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 whitespace-nowrap">
          📷 Check-In Photos
        </h3>
        <div className="flex-1 border-t border-dark-700" />
        {!readOnly && photos.length > 0 && (
          <span className="text-xs text-gray-600">{photos.length}/{MAX_PHOTOS}</span>
        )}
      </div>

      {/* Loading skeleton */}
      {(isLoading || urlsLoading) && photos.length === 0 && (
        <div className="flex gap-2">
          {[1, 2, 3].map(n => (
            <div key={n} className="w-20 h-20 rounded bg-dark-700 animate-pulse" />
          ))}
        </div>
      )}

      {/* Thumbnail grid */}
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {photos.map((photo, i) => (
            <div key={photo.photoId} className="relative group">
              <button
                type="button"
                onClick={() => setLightboxIndex(i)}
                className="block w-20 h-20 rounded overflow-hidden border border-dark-600 hover:border-gold-500 transition focus:outline-none focus:ring-2 focus:ring-gold-500"
                aria-label={`View photo ${i + 1}`}
              >
                {objectUrls[photo.photoId] ? (
                  <img
                    src={objectUrls[photo.photoId]}
                    alt={photo.originalFileName ?? `Photo ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-dark-700 text-gray-600">
                    <span className="text-xl">📷</span>
                  </div>
                )}
              </button>

              {/* Delete overlay — athletes only */}
              {!readOnly && (
                <button
                  type="button"
                  onClick={e => handleDeleteFromThumbnail(e, photo)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-700 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition focus:opacity-100"
                  aria-label="Delete photo"
                  disabled={deleteMutation.isPending}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Inline delete confirmation for thumbnail × clicks */}
      {confirmDeletePhoto && (
        <div className="mb-3 rounded-lg border border-red-500/40 bg-red-950/30 p-3">
          <p className="text-sm text-red-200 mb-2">
            Delete <span className="font-medium">{confirmDeletePhoto.originalFileName ?? 'this photo'}</span>? This cannot be undone.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleConfirmThumbnailDelete}
              disabled={deleteMutation.isPending}
              className="px-3 py-1 rounded text-sm font-semibold bg-red-700 hover:bg-red-600 text-white transition disabled:opacity-50"
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Yes, Delete'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmDeletePhoto(null)}
              className="px-3 py-1 rounded text-sm font-semibold bg-dark-700 hover:bg-dark-600 border border-dark-500 text-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Upload button — athletes only */}
      {canAddMore && (
        <div>
          {/* multiple allows OS file picker multi-select; mobile gets camera roll multi-select */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex items-center gap-2 px-3 py-1.5 rounded text-sm border border-dark-600 text-gray-400 hover:border-gold-500 hover:text-gold-400 transition disabled:opacity-50"
          >
            {isUploading ? (
              <>
                <span className="w-4 h-4 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" />
                Uploading {batchUpload!.current} of {batchUpload!.total}…
              </>
            ) : (
              <>
                <span>📷</span>
                {photos.length === 0 ? 'Add photos' : 'Add more photos'}
              </>
            )}
          </button>
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <p className="mt-2 text-sm text-red-400">{uploadError}</p>
      )}

      {/* Cap warning (some files skipped due to limit) */}
      {uploadWarn && (
        <p className="mt-2 text-sm text-yellow-400">{uploadWarn}</p>
      )}

      {/* Max photos notice */}
      {!readOnly && photos.length >= MAX_PHOTOS && (
        <p className="text-xs text-gray-600 mt-1">Maximum of {MAX_PHOTOS} photos reached.</p>
      )}

      {/* Empty state for read-only (coach) view */}
      {readOnly && photos.length === 0 && !isLoading && (
        <p className="text-sm text-gray-600 italic">No photos for this check-in.</p>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={photos}
          objectUrls={objectUrls}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onDelete={readOnly ? undefined : handleDeleteFromLightbox}
        />
      )}
    </div>
  );
};

export default CheckInPhotoSection;
